import { IUrlFilter, IFilter } from 'api/types/filter'
import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

export type UseUrlFiltersOutput = {
  urlFilters: Array<IUrlFilter>
  setUrlFilters: (value: {
    initialFilters?: Array<IFilter>
    isSelected: boolean
    selectedFilter: IUrlFilter
  }) => void
}

export const useUrlFilters = (): UseUrlFiltersOutput => {
  const [searchParams, setSearchParams] = useSearchParams()
  const filters = searchParams.get('filters')

  const urlFilters = useMemo(() => {
    if (!filters) return []

    try {
      const decoded = decodeURIComponent(filters)

      return decoded.split('&').map((item) => {
        const [category, id, title] = item.split('+')

        return { category, id, title }
      })
    } catch {
      return []
    }
  }, [filters])

  const handleSelectFilter = useCallback(
    ({
      isSelected,
      selectedFilter,
    }: {
      isSelected: boolean
      selectedFilter: IUrlFilter
    }) => {
      setSearchParams((prev) => {
        if (!isSelected && !selectedFilter.id) {
          prev.delete('filters')
        } else {
          let filters = [...urlFilters]

          prev.delete('query')

          if (isSelected) {
            filters = [...filters, selectedFilter]
          } else {
            filters = filters.filter((item) => item.id !== selectedFilter.id)
          }

          if (filters.length === 0) {
            prev.delete('filters')
          } else {
            const raw = filters
              .map((f) => `${f.category}+${f.id}+${f.title}`)
              .join('&')
            prev.set('filters', encodeURIComponent(raw))
          }
        }

        return prev
      })
    },
    [setSearchParams, urlFilters],
  )

  return {
    urlFilters,
    setUrlFilters: handleSelectFilter,
  }
}
